/**
 * Funzioni di calcolo per Ravvedimento Operoso e Rateizzazione
 */

import {
  FASCE_SANZIONE_PRE_RIFORMA,
  FASCE_SANZIONE_POST_RIFORMA,
  DATA_RIFORMA_SANZIONI,
  TASSI_LEGALI,
  MAGGIORAZIONE_RATA,
  SCADENZE_RATE,
} from "../constants/ravvedimento";

/**
 * Seleziona il set di fasce sanzione applicabile in base alla data di scadenza.
 * La riforma D.Lgs. 87/2024 ha ridotto la sanzione base dal 30% al 25% per le
 * violazioni con scadenza dal 01/09/2024.
 * @param {string} dataScadenza - Data scadenza in formato YYYY-MM-DD
 * @returns {Array} Set di fasce PRE o POST riforma
 */
export function getFasceSanzione(dataScadenza) {
  const scadenza = new Date(dataScadenza);
  const riforma = new Date(DATA_RIFORMA_SANZIONI);
  // dataScadenza assente/invalida → default prudente al set PRE-riforma (storico)
  return scadenza >= riforma
    ? FASCE_SANZIONE_POST_RIFORMA
    : FASCE_SANZIONE_PRE_RIFORMA;
}

/**
 * Calcola i giorni di ritardo tra la scadenza e la data di pagamento
 * @param {string} dataScadenza - Data scadenza in formato YYYY-MM-DD
 * @param {string} dataPagamento - Data pagamento in formato YYYY-MM-DD
 * @returns {number} Numero di giorni di ritardo (0 se pagamento puntuale o anticipato)
 */
export function calcolaGiorniRitardo(dataScadenza, dataPagamento) {
  const scadenza = new Date(dataScadenza);
  const pagamento = new Date(dataPagamento);

  // Reset ore per evitare problemi di timezone
  scadenza.setHours(0, 0, 0, 0);
  pagamento.setHours(0, 0, 0, 0);

  const diffMs = pagamento - scadenza;
  const diffGiorni = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return Math.max(0, diffGiorni);
}

/**
 * Trova la fascia di sanzione applicabile in base ai giorni di ritardo
 * @param {number} giorniRitardo - Numero di giorni di ritardo
 * @param {string} dataScadenza - Data scadenza in formato YYYY-MM-DD (seleziona set pre/post riforma)
 * @returns {object|null} Fascia sanzione o null se non applicabile
 */
export function getFasciaSanzione(giorniRitardo, dataScadenza) {
  if (giorniRitardo <= 0) return null;

  const fasce = getFasceSanzione(dataScadenza);
  return fasce.find(
    (f) => giorniRitardo >= f.minGiorni && giorniRitardo <= f.maxGiorni
  );
}

/**
 * Ottiene la descrizione testuale della fascia di ravvedimento
 * @param {number} giorniRitardo - Numero di giorni di ritardo
 * @param {string} dataScadenza - Data scadenza in formato YYYY-MM-DD (seleziona set pre/post riforma)
 * @returns {string} Descrizione della fascia
 */
export function getDescrizioneFascia(giorniRitardo, dataScadenza) {
  if (giorniRitardo <= 0) return "Pagamento puntuale";

  const fascia = getFasciaSanzione(giorniRitardo, dataScadenza);
  return fascia ? fascia.descrizione : "Fascia non trovata";
}

/**
 * Calcola la sanzione in base all'importo e ai giorni di ritardo
 * @param {number} importo - Importo dovuto in euro
 * @param {number} giorniRitardo - Numero di giorni di ritardo
 * @param {string} dataScadenza - Data scadenza in formato YYYY-MM-DD (seleziona set pre/post riforma)
 * @returns {number} Importo sanzione arrotondato a 2 decimali
 */
export function calcolaSanzione(importo, giorniRitardo, dataScadenza) {
  if (giorniRitardo <= 0 || importo <= 0) return 0;

  const fascia = getFasciaSanzione(giorniRitardo, dataScadenza);
  if (!fascia) return 0;

  let sanzione;
  if (fascia.tipo === "giornaliero") {
    // Ravvedimento sprint: percentuale giornaliera (1/10 del 1/15 = 0.0667% al giorno)
    sanzione = importo * fascia.percentuale * giorniRitardo;
  } else {
    // Fascia fissa: percentuale totale
    sanzione = importo * fascia.percentuale;
  }

  return Math.round(sanzione * 100) / 100;
}

/**
 * Calcola gli interessi legali considerando eventuali cambi di tasso durante il periodo
 * @param {number} importo - Importo dovuto in euro
 * @param {string} dataScadenza - Data scadenza in formato YYYY-MM-DD
 * @param {string} dataPagamento - Data pagamento in formato YYYY-MM-DD
 * @returns {number} Importo interessi arrotondato a 2 decimali
 */
export function calcolaInteressi(importo, dataScadenza, dataPagamento) {
  if (importo <= 0) return 0;

  const scadenza = new Date(dataScadenza);
  const pagamento = new Date(dataPagamento);

  scadenza.setHours(0, 0, 0, 0);
  pagamento.setHours(0, 0, 0, 0);

  if (pagamento <= scadenza) return 0;

  let interessiTotali = 0;
  let dataCorrente = new Date(scadenza);

  while (dataCorrente < pagamento) {
    const annoCorrente = dataCorrente.getFullYear();
    const tassoAnno = TASSI_LEGALI[annoCorrente] || TASSI_LEGALI[2026]; // fallback

    // Fine anno o data pagamento, quello che viene prima
    const fineAnno = new Date(annoCorrente, 11, 31);
    fineAnno.setHours(0, 0, 0, 0);
    const finePerioodo = fineAnno < pagamento ? fineAnno : pagamento;

    // Giorni nel periodo
    const giorniPeriodo = Math.floor((finePerioodo - dataCorrente) / (1000 * 60 * 60 * 24));

    if (giorniPeriodo > 0) {
      // Interesse = Capitale × Tasso × Giorni / 365
      const interessePeriodo = importo * tassoAnno * giorniPeriodo / 365;
      interessiTotali += interessePeriodo;
    }

    // Passa al 1° gennaio dell'anno successivo
    dataCorrente = new Date(annoCorrente + 1, 0, 1);
  }

  return Math.round(interessiTotali * 100) / 100;
}

/**
 * Restituisce l'aliquota di sanzione del ravvedimento (frazione, es. 0.0125)
 * per i giorni di ritardo, selezionando il set PRE/POST riforma (D.Lgs. 87/2024)
 * in base alla data di scadenza. Per la fascia "sprint" (<=14 gg) restituisce
 * la percentuale cumulata sui giorni di ritardo.
 * @param {string} dataScadenza - Data scadenza in formato YYYY-MM-DD
 * @param {number} giorniRitardo - Numero di giorni di ritardo
 * @returns {number} Aliquota sanzione (0 se pagamento puntuale)
 */
export function getSanzioneRavvedimento(dataScadenza, giorniRitardo) {
  const fascia = getFasciaSanzione(giorniRitardo, dataScadenza);
  if (!fascia) return 0;
  return fascia.tipo === "giornaliero"
    ? fascia.percentuale * giorniRitardo
    : fascia.percentuale;
}

/**
 * Calcola il ravvedimento operoso completo
 * @param {number} importo - Importo dovuto in euro
 * @param {string} dataScadenza - Data scadenza in formato YYYY-MM-DD
 * @param {string} dataPagamento - Data pagamento in formato YYYY-MM-DD
 * @returns {object} Oggetto con dettaglio ravvedimento
 */
export function calcolaRavvedimento(importo, dataScadenza, dataPagamento) {
  const giorniRitardo = calcolaGiorniRitardo(dataScadenza, dataPagamento);
  const fascia = getFasciaSanzione(giorniRitardo, dataScadenza);
  const sanzione = calcolaSanzione(importo, giorniRitardo, dataScadenza);
  const interessi = calcolaInteressi(importo, dataScadenza, dataPagamento);
  const totale = Math.round((importo + sanzione + interessi) * 100) / 100;

  return {
    importoOriginale: importo,
    dataScadenza,
    dataPagamento,
    giorniRitardo,
    fascia: fascia ? fascia.descrizione : null,
    percentualeSanzione: getSanzioneRavvedimento(dataScadenza, giorniRitardo),
    sanzione,
    interessi,
    totale,
    isPuntuale: giorniRitardo === 0,
  };
}

/**
 * Calcola la rateizzazione del saldo di giugno
 * @param {number} importo - Importo totale da rateizzare in euro
 * @param {number} numeroRate - Numero di rate (1-6)
 * @param {number} anno - Anno di riferimento (per calcolare le date)
 * @returns {object} Oggetto con array rate e totali
 */
export function calcolaRateizzazione(importo, numeroRate, anno = new Date().getFullYear()) {
  if (importo <= 0) {
    return {
      importoOriginale: 0,
      numeroRate: 0,
      rate: [],
      totaleMaggiorazioni: 0,
      totaleComplessivo: 0,
    };
  }

  // Limita le rate tra 1 e 6
  const numRate = Math.min(Math.max(1, Math.round(numeroRate)), 6);

  // Importo base per rata (prima delle maggiorazioni)
  const importoBaseRata = Math.round((importo / numRate) * 100) / 100;

  const rate = [];
  let totaleMaggiorazioni = 0;

  for (let i = 0; i < numRate; i++) {
    const scadenza = SCADENZE_RATE[i];

    // Per la prima rata non c'è maggiorazione
    // Dalla seconda in poi: 0.33% per ogni mese trascorso dalla prima rata
    const mesiTrascorsi = i; // 0 per prima rata, 1 per seconda, ecc.
    const maggiorazionePercentuale = mesiTrascorsi * MAGGIORAZIONE_RATA;
    const maggiorazione = Math.round(importoBaseRata * maggiorazionePercentuale * 100) / 100;
    const totaleRata = Math.round((importoBaseRata + maggiorazione) * 100) / 100;

    totaleMaggiorazioni += maggiorazione;

    // Calcola data effettiva
    const [giorno, mese] = scadenza.data.split("/").map(Number);
    const dataScadenza = new Date(anno, mese - 1, giorno);

    rate.push({
      numero: i + 1,
      scadenza: scadenza.data,
      dataScadenza: dataScadenza.toISOString().split("T")[0],
      descrizione: scadenza.descrizione,
      importoBase: importoBaseRata,
      mesiTrascorsi,
      maggiorazionePercentuale: Math.round(maggiorazionePercentuale * 10000) / 100, // in %
      maggiorazione,
      totale: totaleRata,
    });
  }

  // Aggiusta arrotondamenti sulla prima rata
  const sommaRate = rate.reduce((acc, r) => acc + r.importoBase, 0);
  const diffArrotondamento = Math.round((importo - sommaRate) * 100) / 100;
  if (diffArrotondamento !== 0 && rate.length > 0) {
    rate[0].importoBase = Math.round((rate[0].importoBase + diffArrotondamento) * 100) / 100;
    rate[0].totale = Math.round((rate[0].importoBase + rate[0].maggiorazione) * 100) / 100;
  }

  const totaleComplessivo = rate.reduce((acc, r) => acc + r.totale, 0);

  return {
    importoOriginale: importo,
    numeroRate: numRate,
    anno,
    rate,
    totaleMaggiorazioni: Math.round(totaleMaggiorazioni * 100) / 100,
    totaleComplessivo: Math.round(totaleComplessivo * 100) / 100,
  };
}

/**
 * Formatta un importo in euro con formato italiano
 * @param {number} importo - Importo da formattare
 * @returns {string} Importo formattato (es. "1.234,56 €")
 */
export function formatEuro(importo) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(importo);
}

/**
 * Formatta una percentuale con formato italiano
 * @param {number} percentuale - Percentuale come decimale (es. 0.015 per 1.5%)
 * @returns {string} Percentuale formattata (es. "1,50%")
 */
export function formatPercentuale(percentuale) {
  return new Intl.NumberFormat("it-IT", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(percentuale);
}
